/**
 * TaskFormContainer Component
 *
 * Main orchestrator for the Task Form.
 * Contains all state, handlers, and effects.
 * Renders sub-components with appropriate props.
 *
 * Decomposed from TaskForm.js for maintainability.
 * Original: 2,431 lines -> Container: ~800 lines + 5 sub-components
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Card, Col, Row, Typography, Form, Input, Modal, Button, message } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useTaskContext } from '../../../../../core/components/tasks/useTaskContext';
import { useQuery } from 'react-apollo';
import GET_SLA_CONFIGS from '../../../../../core/GraphQl/Queries/GET_SLA_CONFIGS';
import { withRouter } from 'react-router-dom';
import { debounce } from 'lodash';
import moment from 'moment';

// Layout components
import { PageContentLayout, PageHeaderLayout } from '../../../../shared/layout';
import OverlaySpinner from '../../../../shared/OverlaySpinner';

// Modal components
import {
  TaskDatePickerModal,
  TaskStartDatePickerModal,
  TaskCodeSnippetModal,
  TaskRelatedCampaignsModal,
} from '../modals';

// Context providers
import { useOrgContext } from '../../../../../core/components/orgs/useOrgContext';
import { useProjectContext } from '../../../../../core/components/projects/useProjectContext';
import { useUserContext } from '../../../../../core/components/users/useUserContext';

// Utilities
import { uploadFileToS3 } from '../../../../shared/upload/uploadFileToS3';
import { subTypes, campaignLinkedRequestTypes } from '../../configs/task.config';
import {
  hasCampaignsFeature,
  CHANNEL_PARTNER_LEVELS,
  getAddedMentionedUserIds,
  getTaskProjectAssigneeGroups,
} from '../../utils/task.utils';
import { useParams, useLocation } from 'react-router-dom';
import { getFileType } from '../../../../../core/utils/constants/constants';
import { ENABLED_USER_FILTER } from '../../../../../core/utils/enabledUserFilter';
import useResponsiveTask from '../../hooks/useResponsiveTask';

// Sub-components
import TaskBasicFields from './TaskBasicFields';
import TaskAssignmentFields from './TaskAssignmentFields';
import TaskDescriptionField from './TaskDescriptionField';
import TaskCampaignsField from './TaskCampaignsField';
import TaskFormActions from './TaskFormActions';
import { getRequestTypeDef, DynamicRequestFields } from './requestTypes';
import { consumeTaskFormDraft } from './taskFormDraft';

// Styles
import { fontSize as fontSizeTokens, bg } from '../../configs/designTokens';
import '../../styles/index.scss';

const { Text } = Typography;

const TaskFormContainer = ({ history, isModal, onClose, onSuccess, projectId, form: externalForm, captureDraftRef }) => {
  // URL parameters (route mode only). In modal mode the form is create-only and
  // self-contained: the route params/query string belong to the page BEHIND the
  // modal (e.g. the task board) and must not leak into the form.
  const { id: routeTaskId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const currentTaskId = isModal ? 'new' : routeTaskId;
  const linkedProjectId = isModal ? projectId || null : queryParams.get('projectId');
  const linkedCampaignId = isModal ? null : queryParams.get('campaignId');
  // 'new' is the create sentinel (route /tasks/form/new and the modal), not a task id
  const isEditMode = Boolean(currentTaskId && currentTaskId !== 'new');

  // Form instance. The modal passes its own instance in (so it can check dirty state for the
  // unsaved-changes prompt); the full-page route uses the internal one.
  const [internalForm] = Form.useForm();
  const form = externalForm || internalForm;

  // Refs
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  // Context hooks
  const {
    currentUser,
    createTask,
    fetchTask,
    currentTask,
    taskLoading,
    updateTask,
    updateTaskLoading,
    fetchCampaignForTask,
    linkedCampaign,
    setLinkedCampaign,
    fetchTeams,
    teamsData,
    teamsLoading,
    fetchMentionUsers,
    mentionUsers,
  } = useTaskContext();

  // The form's PRIVATE org query slot (see OrgProvider) — the shared
  // fetchBasicOrgsDetails result backs the task board's advertiser filter, and the
  // form's Channel Partner/Advertiser fetches were clobbering it from the modal.
  const {
    fetchFormOrgsDetails: fetchBasicOrgsDetails,
    formOrgsDetails: basicOrgsDetails,
    formOrgsDetailsLoading: basicOrgsDetailsLoading,
  } = useOrgContext();
  const { fetchBasicUsersDetails, basicUsersDetails, basicUsersDetailsLoading } = useUserContext();
  const { fetchProject, currentProject, fetchProjectsPaginated, projectsConnectionData } =
    useProjectContext();

  // SLA configs
  const { data: slaData } = useQuery(GET_SLA_CONFIGS);
  const slaConfigMap = useMemo(() => {
    if (!slaData?.slaConfigs) return {};
    return slaData.slaConfigs.reduce((map, config) => {
      map[config.requestType] = config.recommendedDays;
      return map;
    }, {});
  }, [slaData]);
  const [selectedRequestType, setSelectedRequestType] = useState('');

  // Window width for responsive layout
  const { width: windowWidth, isMobile, isDesktop } = useResponsiveTask();

  // User/Org derived values
  const userOrgName = currentUser?.role?.org?.name;
  const level = currentUser?.role?.org?.level;
  const hasCampaigns = hasCampaignsFeature(currentUser);

  // ============================================
  // STATE DECLARATIONS
  // ============================================

  // Initial state tracking (for detecting deletions during update)
  const [initialAttachedFiles, setInitialAttachedFiles] = useState([]);
  const [initialAttachedImages, setInitialAttachedImages] = useState([]);
  const [initialRelatedCampaigns, setInitialRelatedCampaigns] = useState([]);

  // New attachments tracking (for update mode)
  const [newAttachedFiles, setNewAttachedFiles] = useState([]);
  const [newAttachedImages, setNewAttachedImages] = useState([]);

  // Form control state
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [createMoreTasks, setCreateMoreTasks] = useState(false);

  // Org/User selection state
  const [assigneeOrgs, setAssigneeOrgs] = useState([]);
  const [channelPartnerOrgs, setChannelPartnerOrgs] = useState([]);
  const [advertiserOrgs, setAdvertiserOrgs] = useState([]);
  const [assigneeUsers, setAssigneeUsers] = useState(basicUsersDetails || []);
  const [orgTypeRequest, setOrgTypeRequest] = useState('');

  // Team selection state
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  // Project selection state
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(linkedProjectId || null);
  const [projectAssigneeGroups, setProjectAssigneeGroups] = useState([]);
  const [selectedAdditionalProjectIds, setSelectedAdditionalProjectIds] = useState([]);

  // Request type/subtype state
  const [filteredSubTypes, setFilteredSubTypes] = useState([]);
  const [isSubTypeDisabled, setIsSubTypeDisabled] = useState(true);

  // File attachments state
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attachedImages, setAttachedImages] = useState([]);

  // Code snippet state
  const [showCodeSnippetModal, setShowCodeSnippetModal] = useState(false);
  const [attachedCodeSnippet, setAttachedCodeSnippet] = useState('');

  // Related campaigns state
  const [showRelatedCampaignModal, setRelatedCampaignModal] = useState(false);
  const [relatedCampaigns, setRelatedCampaigns] = useState([]);

  // Date picker modal state
  const [taskDetailVisible, setTaskDatePickerVisible] = useState(false);
  const [taskStartDatePickerVisible, setTaskStartDatePickerVisible] = useState(false);
  const [dueDateRecurringConfig, setDueDateRecurringConfig] = useState({});

  // Collaborator/Follower modal state
  const [showCollaboratorModal, setShowCollaboratorModal] = useState(false);
  const [selectedCollaboratorId, setSelectedCollaboratorId] = useState(null);
  const [showFollowerModal, setShowFollowerModal] = useState(false);
  const [selectedFollowerId, setSelectedFollowerId] = useState(null);

  // ============================================
  // HANDLERS
  // ============================================

  const handleSubTypeChange = (value) => {
    // value is the [requestType, subType] cascade path (or a bare request type)
    const path = Array.isArray(value) ? value : value ? [value] : [];
    const reqType = path[0];
    const reqSubType = path[1];
    const subTypesForSelected = subTypes[reqType] || [];
    setFilteredSubTypes(subTypesForSelected);
    setIsSubTypeDisabled(subTypesForSelected.length === 0);
    setSelectedRequestType(reqType || '');
    form.setFieldsValue({
      requestType: reqType || undefined,
      requestSubType: reqSubType || undefined,
    });
  };

  // DOM <textarea> node — works for both Input.TextArea and Mentions refs.
  const getTaskTextarea = () =>
    (textareaRef.current &&
      (textareaRef.current.textarea ||
        (textareaRef.current.resizableTextArea &&
          textareaRef.current.resizableTextArea.textArea))) ||
    null;

  const mentionOrgId = currentUser?.role?.org?.id;

  // Load task-management users in the current org for the @ dropdown.
  useEffect(() => {
    if (!mentionOrgId) return;
    fetchMentionUsers({
      variables: {
        first: 200,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: { id: mentionOrgId },
                roleItems_some: { feature: 'TASK_MANAGEMENT' },
              },
            },
            ENABLED_USER_FILTER,
          ],
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentionOrgId]);

  // Map @username mentions across the given texts back to user IDs, matching the backend's
  // boundary so trailing punctuation (e.g. "@user.") resolves. Longest username first,
  // blanking each match so a shorter prefix (e.g. "@randall" inside "@randall.tan") can't
  // double-count.
  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const getMentionedUserIds = (...texts) => {
    let remaining = texts.filter(Boolean).join('\n');
    if (!remaining) return [];
    const ids = [];
    [...(mentionUsers || [])]
      .filter((u) => u.username)
      .sort((a, b) => b.username.length - a.username.length)
      .forEach((u) => {
        const re = new RegExp(`@${escapeRegExp(u.username)}(?![A-Za-z0-9_])`, 'g');
        const replaced = remaining.replace(re, ' ');
        if (replaced !== remaining) {
          ids.push(u.id);
          remaining = replaced;
        }
      });
    return ids;
  };

  const handleInsertCode = () => {
    const textarea = getTaskTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = form.getFieldValue('taskDetails') || '';
    const selectedText = currentValue.slice(start, end);

    let newValue, cursorPosition;

    if (selectedText) {
      newValue =
        currentValue.slice(0, start) + '```\n' + selectedText + '\n```' + currentValue.slice(end);
      cursorPosition = start + selectedText.length + 8;
    } else {
      newValue = currentValue.slice(0, start) + '```\n\n```' + currentValue.slice(end);
      cursorPosition = start + 4;
    }

    form.setFieldsValue({ taskDetails: newValue });

    setTimeout(() => {
      textarea.selectionStart = cursorPosition;
      textarea.selectionEnd = cursorPosition;
      textarea.focus();
    }, 0);
  };

  const insertText = (options) => {
    const textarea = getTaskTextarea();
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const selectedText = value.substring(start, end);

    let transformedText, newStart, newEnd;

    if (options.transform) {
      const result = options.transform(selectedText, start, end);
      transformedText = result.text;
      newStart = result.newStart;
      newEnd = result.newEnd;
    } else {
      const { before, after } = options;
      transformedText = before + selectedText + after;
      newStart = start + before.length;
      newEnd = newStart + selectedText.length;
    }

    const newValue = value.substring(0, start) + transformedText + value.substring(end);
    form.setFieldsValue({ taskDetails: newValue });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = getTaskTextarea();
      if (!textarea) return;

      const value = form.getFieldValue('taskDetails') || '';
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = value.indexOf('\n', start);
      const currentLine = value.slice(lineStart, lineEnd === -1 ? value.length : lineEnd);

      const unorderedMatch = currentLine.match(/^-\s/);
      const orderedMatch = currentLine.match(/^(\d+)\.\s/);

      if (unorderedMatch || orderedMatch) {
        e.preventDefault();
        let newValue, newCursorPos;

        if (unorderedMatch) {
          newValue = `${value.slice(0, start)}\n- ${value.slice(end)}`;
          newCursorPos = start + 3;
        } else {
          const nextNumber = parseInt(orderedMatch[1]) + 1;
          newValue = `${value.slice(0, start)}\n${nextNumber}. ${value.slice(end)}`;
          newCursorPos = start + `${nextNumber}. `.length + 1;
        }

        form.setFieldsValue({ taskDetails: newValue });
        setTimeout(() => {
          textarea.selectionStart = newCursorPos;
          textarea.selectionEnd = newCursorPos;
        }, 0);
      }
    }
  };

  const handleUploadFilesToS3 = async (files) => {
    try {
      const medias = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const media = await uploadFileToS3({ file });
        medias.push({
          ...media,
          fileType: getFileType(file.name),
        });
      }
      return medias;
    } catch (error) {
      console.error('handleUploadFilesToS3 failed:', error);
      throw error;
    }
  };

  // Remove by object reference, not id: freshly-attached files are raw File objects with no
  // id, so filtering by id would drop every new file at once (they all share id === undefined).
  const handleFileDelete = (fileToRemove, type) => {
    if (type === 'file') {
      setAttachedFiles(attachedFiles.filter((file) => file !== fileToRemove));
      setNewAttachedFiles(newAttachedFiles.filter((file) => file !== fileToRemove));
    } else if (type === 'image') {
      setAttachedImages(attachedImages.filter((image) => image !== fileToRemove));
      setNewAttachedImages(newAttachedImages.filter((image) => image !== fileToRemove));
    }
  };

  // ============================================
  // FETCH FUNCTIONS
  // ============================================

  const fetchBasicOrgs = debounce((name = '', type = 'ASSIGNEE') => {
    if (type === 'CHANNEL_PARTNER') {
      setOrgTypeRequest('CHANNEL_PARTNER');
      fetchBasicOrgsDetails({
        variables: {
          first: 300,
          skip: 0,
          where: {
            AND: [
              { name_contains: name },
              {
                OR: [
                  // Child orgs in my hierarchy, restricted to the channel-partner levels.
                  {
                    AND: [
                      { level_in: [...CHANNEL_PARTNER_LEVELS, 'CORE', 'ENTERPRISE'] },
                      { parentOrg_some: { id: currentUser.role.org.id } },
                    ],
                  },
                  // My own org is ALWAYS selectable (any level) so a task can still be created
                  // against the logged-in org even when it has no child orgs (e.g. mid org-cleanup,
                  // or a PLATFORM org whose level isn't in the channel-partner set).
                  { id: currentUser.role.org.id },
                ],
              },
            ],
          },
        },
      });
    } else if (type === 'ADVERTISER') {
      const channelPartnerId =
        level !== 'PLATFORM' ? currentUser.role.org.id : form.getFieldValue('channelPartnerId');
      if (!channelPartnerId) {
        setAdvertiserOrgs([]);
        return;
      }
      setOrgTypeRequest('ADVERTISER');
      fetchBasicOrgsDetails({
        variables: {
          first: 1000,
          skip: 0,
          where: {
            AND: [
              {
                OR: [{ parentOrg_some: { id: channelPartnerId } }, { id: channelPartnerId }],
              },
              { name_contains: name },
            ],
          },
        },
      });
    } else {
      setOrgTypeRequest('ASSIGNEE');
      fetchBasicOrgsDetails({
        variables: {
          first: 10,
          skip: 0,
          where: {
            AND: [
              {
                OR: [
                  { parentOrg_some: { id: currentUser.role.org.id } },
                  { id: currentUser.role.org.id },
                ],
                name_contains: name,
              },
            ],
            OR: [
              {
                roles_some: {
                  org: { level: 'PLATFORM' },
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                },
              },
            ],
          },
        },
      });
    }
  }, 500);

  const fetchBasicUsers = debounce((name = '') => {
    // Assignment is team-based now (no assignee-org field). Default to all assignable users in
    // the current user's org so an existing/legacy assignee is still shown; the assignee field
    // narrows to a team's members only once a team is selected.
    const orgId = form.getFieldValue('assigneeOrgId') || currentUser?.role?.org?.id;
    if (!orgId) {
      setAssigneeUsers([]);
      return;
    }
    fetchBasicUsersDetails({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            {
              roles_some: {
                org: { id: orgId, level: 'PLATFORM' },
                roleItems_some: { feature: 'TASK_MANAGEMENT' },
              },
              name_contains: name,
            },
            ENABLED_USER_FILTER,
          ],
        },
      },
    });
  }, 500);

  const fetchTeamsByOrg = debounce((orgId, name = '') => {
    if (!orgId) {
      setTeams([]);
      return;
    }
    fetchTeams({
      variables: {
        first: 100,
        skip: 0,
        where: {
          AND: [
            { org: { id: orgId } },
            ...(name ? [{ name_contains: name }] : []),
          ],
        },
      },
    });
  }, 500);

  const fetchProjects = debounce((name = '') => {
    fetchProjectsPaginated({
      variables: {
        first: 100,
        skip: 0,
        where: {
          name_contains: name,
          owner: {
            roles_some: {
              org: { OR: [{ id: currentUser?.role?.org?.id }] },
            },
          },
        },
      },
    });
  }, 500);

  // ============================================
  // SUBMIT HANDLER
  // ============================================

  const handleSubmit = async () => {
    try {
      await form.validateFields();
    } catch (errorFields) {
      throw errorFields;
    }

    const {
      name,
      startDate,
      dueDate,
      requestType,
      requestSubType,
      linkUrl,
      urgency,
      assigneeOrgId,
      assigneeUserId: selectedAssignees,
      advertiserId,
      taskDetails,
      relatedCampaigns,
      teamId: selectedFormTeamId,
    } = form.getFieldsValue();

    // Read the project fields from the store rather than getFieldsValue(): the selects only render
    // for PLATFORM, and treating an unmounted field as empty would unlink the task on every edit.
    const formProjectId = form.getFieldValue('linkedProjectId') || null;
    const additionalProjectIds = form.getFieldValue('additionalProjectIds') || [];

    let channelPartnerId = form.getFieldValue('channelPartnerId');

    const {
      recurring,
      recurrenceType,
      dateOfMonth,
      dayOfMonth,
      dayOfWeek,
      monthlyType,
      weeklyDays,
      yearlyDate,
      yearlyMonth,
    } = dueDateRecurringConfig;

    const createdBy = { connect: { id: currentUser.id } };

    // Detect deletions
    const filesToDelete = initialAttachedFiles.filter(
      (file) => !attachedFiles.some((f) => f.id === file.id)
    );
    const imagesToDelete = initialAttachedImages.filter(
      (image) => !attachedImages.some((i) => i.id === image.id)
    );
    const campaignsToDelete = initialRelatedCampaigns.filter(
      (c) => !(relatedCampaigns || []).some((rc) => rc.id === c.id)
    );

    let mediaFiles = [];
    let mediaImages = [];

    try {
      if (currentTaskId && currentTaskId !== 'new') {
        if (newAttachedFiles.length > 0) {
          mediaFiles = await handleUploadFilesToS3(newAttachedFiles);
        }
        if (newAttachedImages.length > 0) {
          mediaImages = await handleUploadFilesToS3(newAttachedImages);
        }
      } else {
        if (attachedFiles.length > 0) {
          mediaFiles = await handleUploadFilesToS3(attachedFiles);
        }
        if (attachedImages.length > 0) {
          mediaImages = await handleUploadFilesToS3(attachedImages);
        }
      }
    } catch (error) {
      message.error(error.message);
      return;
    }

    // Non-PLATFORM users have the Channel Partner locked to their own org (the field is disabled),
    // so persist it explicitly. This keeps Direct detection consistent between the form and the
    // saved task's details view (isDirectTask reads the saved channelPartner).
    if (!channelPartnerId && level !== 'PLATFORM') {
      channelPartnerId = currentUser.role.org.id;
    }

    // Build assignee users connection
    const assigneeUserIds = selectedAssignees;
    const previousAssigneeUsers = currentTask?.assigneeUsers || [];
    const previousAssigneeUsersIds = previousAssigneeUsers.map((user) => user.id);
    const assigneeUsers = {};
    const collaborators = currentTask?.collaborators?.map((u) => u.id) || [];
    const followers = currentTask?.followers?.map((u) => u.id) || [];

    const assigneeIdToCheck = Array.isArray(assigneeUserIds)
      ? assigneeUserIds[0]?.key || assigneeUserIds[0]
      : assigneeUserIds;

    // Auto-promote: if new assignee is a collaborator, auto-disconnect from collaborators.
    // Edit mode only — in create mode `currentTask` is shared state and may hold a stale
    // task (e.g. last one opened in the drawer), so its collaborators/followers don't apply.
    let autoDisconnectCollaborator = null;
    if (
      isEditMode &&
      assigneeIdToCheck &&
      collaborators.includes(assigneeIdToCheck) &&
      assigneeUserIds?.length > 0
    ) {
      autoDisconnectCollaborator = assigneeIdToCheck;
    }

    if (
      isEditMode &&
      assigneeIdToCheck &&
      followers.includes(assigneeIdToCheck) &&
      assigneeUserIds?.length > 0
    ) {
      message.warning('Assignee is already a follower. Please remove them from followers first.');
      return;
    }

    if (currentTaskId && currentTaskId !== 'new') {
      if (assigneeUserIds && previousAssigneeUsers && !assigneeUserIds[0]?.key) {
        const usersToDisconnect = previousAssigneeUsersIds.filter(
          (id) => !assigneeUserIds.includes(id)
        );
        if (usersToDisconnect.length > 0) {
          assigneeUsers.disconnect = usersToDisconnect.map((id) => ({ id }));
        }
      }
      if (assigneeUserIds && (!Array.isArray(assigneeUserIds) || assigneeUserIds.length > 0) && !previousAssigneeUsersIds.includes(assigneeUserIds) && !assigneeUserIds[0]?.key) {
        assigneeUsers.connect = { id: assigneeUserIds };
      }
    } else {
      if (assigneeUserIds?.length > 0 && !assigneeUserIds[0]?.key) {
        assigneeUsers.connect = { id: assigneeUserIds };
      }
    }

    // Auto-collaborator logic: when a team is selected, add its subscribers and subscriberTeam
    // members as collaborators. Runs on create, and on edit when the team is CHANGED to a different
    // one ("add new, keep old" — the new team's subscribers are added; existing collaborators,
    // including the previous team's, are never removed here).
    // - Includes the creator if they're a subscriber (individual or via a team they belong to).
    // - Excludes anyone already a member of the target team (they're part of the team itself).
    // - Excludes the assignee (you can't be both assignee and collaborator).
    // - On edit, excludes people who are already collaborators (avoid redundant connects).
    const desiredProjectIds = [
      ...new Set([formProjectId, ...(additionalProjectIds || [])].filter(Boolean)),
    ];
    // A task that is on any project always has a primary — it drives the assignee pool, the
    // optional-due-date rule and the post-create redirect. If none was picked, the first
    // selected project takes the role rather than leaving the task in a degraded state.
    const effectivePrimaryId = formProjectId || desiredProjectIds[0] || null;

    const previousTeamId = currentTask?.teams?.[0]?.id || null;
    // A task linked to a project is assigned via the project, not a team (projects and teams are
    // alternative groupings). So whenever a project is selected, ignore any team still set in the
    // form — including a value antd preserved from a task that loaded already linked to a project —
    // so the task isn't left on a team and skips team-derived side effects like auto-collaborators.
    const effectiveTeamId = effectivePrimaryId ? null : selectedFormTeamId;
    const teamChanged = isEditMode && !!effectiveTeamId && effectiveTeamId !== previousTeamId;
    let autoCollaboratorIds = [];
    if (effectiveTeamId && (!isEditMode || teamChanged)) {
      const selectedTeam = (teams || []).find((t) => t.id === effectiveTeamId);
      if (selectedTeam) {
        const targetTeamMemberIds = new Set((selectedTeam.members || []).map((m) => m.id));

        // Individual subscribers of this team
        const individualSubscribers = (selectedTeam.subscribers || []).map((s) => s.id);

        // Members of teams that subscribed to this team (subscriberTeams)
        const teamSubscriberMembers = (selectedTeam.subscriberTeams || [])
          .flatMap((subscribingTeam) => (subscribingTeam.members || []).map((m) => m.id));

        const assigneeId = Array.isArray(assigneeUserIds) ? assigneeUserIds[0] : assigneeUserIds;
        const existingCollaboratorIds = isEditMode
          ? new Set((currentTask?.collaborators || []).map((c) => c.id))
          : new Set();
        const allAutoCollaborators = [...new Set([...individualSubscribers, ...teamSubscriberMembers])];
        autoCollaboratorIds = allAutoCollaborators.filter(
          (id) => id !== assigneeId && !targetTeamMemberIds.has(id) && !existingCollaboratorIds.has(id)
        );
      }
    }

    // `projects` holds every project the task is assigned to, primary included. Prisma 1 has no
    // `set` on this input, so an edit diffs against what the task already has — and the two lists
    // never overlap, since a connect + disconnect of the same node nets to removed.
    const linkedProjectIds = isEditMode ? (currentTask?.projects || []).map((p) => p.id) : [];
    const projectsToConnect = desiredProjectIds.filter((id) => !linkedProjectIds.includes(id));
    const projectsToDisconnect = linkedProjectIds.filter((id) => !desiredProjectIds.includes(id));

    // Build main task data
    let data = {
      name,
      startDate,
      dueDate,
      createdBy,
      requestType,
      requestSubType,
      linkUrl,
      urgency,
      // Assignment is now Team-based; the form has no assigneeOrg field. On edit, clear any
      // legacy assigneeOrg so a team-assigned task doesn't keep a stale org alongside the team.
      assigneeOrg: effectivePrimaryId
        ? {}
        : assigneeOrgId
          ? { connect: { id: assigneeOrgId } }
          : isEditMode && currentTask?.assigneeOrg
            ? { disconnect: true }
            : {},
      assigneeUsers,
      channelPartner: channelPartnerId ? { connect: { id: channelPartnerId } } : {},
      advertiser: advertiserId ? { connect: { id: advertiserId } } : {},
      attachedFiles: mediaFiles?.length ? { create: mediaFiles } : {},
      attachedImages: mediaImages?.length ? { create: mediaImages } : {},
      relatedCampaigns: relatedCampaigns?.length
        ? { connect: relatedCampaigns.map((c) => ({ id: c.id })) }
        : undefined,
      taskDetails,
      codeSnippet: attachedCodeSnippet,
      status: 'NOT_STARTED',
      // Connect the selected team; when the team is changed on edit, also disconnect the old one
      // (single-team UI — leaving the previous team linked would silently assign two teams).
      // When the team is cleared on edit (e.g. the task is converted to project-linked),
      // disconnect the previous team so it isn't left attached alongside the project.
      teams: effectiveTeamId
        ? (teamChanged && previousTeamId
            ? { connect: [{ id: effectiveTeamId }], disconnect: [{ id: previousTeamId }] }
            : { connect: [{ id: effectiveTeamId }] })
        : (isEditMode && previousTeamId ? { disconnect: [{ id: previousTeamId }] } : {}),
      collaborators: autoCollaboratorIds.length > 0
        ? { connect: autoCollaboratorIds.map((id) => ({ id })) }
        : {},
      project: effectivePrimaryId
        ? { connect: { id: effectivePrimaryId } }
        : isEditMode && currentTask?.project
          ? { disconnect: true }
          : {},
      projects: {
        ...(projectsToConnect.length
          ? { connect: projectsToConnect.map((id) => ({ id })) }
          : {}),
        ...(projectsToDisconnect.length
          ? { disconnect: projectsToDisconnect.map((id) => ({ id })) }
          : {}),
      },
    };

    // Request-type-specific fields -> requestTypeDetails Json (+ task side-effects)
    const requestTypeDef = getRequestTypeDef(requestType);
    if (requestTypeDef) {
      // getFieldsValue(true) so values the current layout doesn't render are serialized too —
      // plain getFieldsValue() only returns MOUNTED fields, which silently nulled out anything
      // hidden by a late deal-type change (issue #11760).
      const requestTypeValues = form.getFieldsValue(true);
      data.requestTypeDetails = requestTypeDef.serialize(requestTypeValues);
      if (requestTypeDef.applyToTask) requestTypeDef.applyToTask(requestTypeValues, data);
    }

    // Add recurring configuration
    if (recurring) {
      data = { ...data, recurring, recurrenceType };
      switch (recurrenceType) {
        case 'DAILY':
          data = {
            ...data,
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            weeklyDays: { set: null },
            yearlyDate: null,
            yearlyMonth: null,
          };
          break;
        case 'MONTHLY':
          data =
            monthlyType === 'DATE_OF_MONTH'
              ? {
                  ...data,
                  dateOfMonth,
                  monthlyType,
                  weeklyDays: { set: null },
                  dayOfMonth: null,
                  dayOfWeek: null,
                  yearlyDate: null,
                  yearlyMonth: null,
                }
              : {
                  ...data,
                  dayOfMonth,
                  dayOfWeek: dayOfWeek?.toUpperCase(),
                  monthlyType,
                  dateOfMonth: null,
                  weeklyDays: { set: null },
                  yearlyDate: null,
                  yearlyMonth: null,
                };
          break;
        case 'WEEKLY':
          data = {
            ...data,
            weeklyDays: { set: weeklyDays },
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            yearlyDate: null,
            yearlyMonth: null,
          };
          break;
        case 'BIWEEKLY':
          data = {
            ...data,
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            weeklyDays: { set: null },
            yearlyDate: null,
            yearlyMonth: null,
          };
          break;
        case 'YEARLY':
          data = {
            ...data,
            yearlyDate,
            yearlyMonth,
            dateOfMonth: null,
            dayOfMonth: null,
            dayOfWeek: null,
            monthlyType: null,
            weeklyDays: { set: null },
          };
          break;
        default:
          break;
      }
    }

    try {
      message.loading(
        `${currentTaskId && currentTaskId !== 'new' ? 'Updating' : 'Creating'} task...`,
        0
      );

      if (currentTaskId && currentTaskId !== 'new') {
        // Handle deletions
        if (filesToDelete.length) {
          await updateTask({
            variables: {
              where: { id: currentTaskId },
              data: { attachedFiles: { delete: filesToDelete.map((f) => ({ id: f.id })) } },
            },
          });
        }
        if (imagesToDelete.length) {
          await updateTask({
            variables: {
              where: { id: currentTaskId },
              data: { attachedImages: { delete: imagesToDelete.map((i) => ({ id: i.id })) } },
            },
          });
        }
        if (campaignsToDelete.length) {
          await updateTask({
            variables: {
              where: { id: currentTaskId },
              data: {
                relatedCampaigns: { disconnect: campaignsToDelete.map((c) => ({ id: c.id })) },
              },
            },
          });
        }

        const { status, createdBy, ...filteredData } = data;
        // Auto-promote: disconnect new assignee from collaborators if needed. Merge with any
        // auto-collaborator connects (e.g. from a team change) so neither clobbers the other.
        if (autoDisconnectCollaborator) {
          filteredData.collaborators = {
            ...filteredData.collaborators,
            disconnect: [{ id: autoDisconnectCollaborator }],
          };
        }
        // Ping only @mentions ADDED in this edit (description + IO note), not ones already present.
        const previousDetails = currentTask?.requestTypeDetails;
        const previousNote =
          previousDetails && typeof previousDetails === 'object' ? previousDetails.note : null;
        const mentions = getAddedMentionedUserIds(
          [currentTask?.taskDetails, previousNote],
          [taskDetails, form.getFieldValue('note')],
          mentionUsers
        );
        await updateTask({
          variables: { where: { id: currentTaskId }, data: filteredData, mentions },
        });
        history.replace(`/tasks/details/${currentTaskId}`);
      } else {
        // @mentions in the description / note ping the right people on create.
        const mentions = getMentionedUserIds(taskDetails, form.getFieldValue('note'));
        await createTask({ variables: { data, mentions } });
        if (onSuccess) onSuccess();

        if (createMoreTasks) {
          const currentValues = form.getFieldsValue();
          form.setFieldsValue({ ...currentValues, name: '' });
          setAttachedFiles([]);
          setAttachedImages([]);
          setNewAttachedFiles([]);
          setNewAttachedImages([]);
          setAttachedCodeSnippet('');
          if (!isModal) window.scrollTo({ top: 0, behavior: 'smooth' });
          message.destroy();
          message.success('Task created successfully. Create another task below.');
        } else {
          if (isModal) {
            // Clear the form first so the modal's unsaved-changes guard doesn't fire on this
            // programmatic close after a successful create.
            form.resetFields();
            if (onClose) onClose();
          } else if (effectivePrimaryId) {
            history.push(`/tasks/projects/details/${effectivePrimaryId}`);
          } else {
            history.push(`/tasks/main/1`);
          }
          message.destroy();
          message.success('Task created successfully');
        }
      }

      if (currentTaskId && currentTaskId !== 'new') {
        message.destroy();
        message.success('Task updated successfully');
      }
    } catch (error) {
      message.destroy();
      message.error(
        `Failed to ${currentTaskId && currentTaskId !== 'new' ? 'update' : 'create'} task: ${error.message}`
      );
      console.error('Task submission error:', error);
    }
  };

  // ============================================
  // EFFECTS
  // ============================================

  // Track initial values for deletion detection (edit mode only — `currentTask` is
  // SHARED TaskProvider state, so in create mode it can hold a stale task left by
  // the drawer/details view and must be ignored)
  useEffect(() => {
    if (currentTask && currentTaskId && currentTaskId !== 'new') {
      setInitialAttachedFiles(currentTask.attachedFiles || []);
      setInitialAttachedImages(currentTask.attachedImages || []);
      setInitialRelatedCampaigns(currentTask.relatedCampaigns || []);
    }
  }, [currentTask, currentTaskId]);

  // Update subtypes when editing existing task
  useEffect(() => {
    if (isEditMode && currentTask) {
      const subTypesForSelected = subTypes[currentTask.requestType] || [];
      setFilteredSubTypes(subTypesForSelected);
      setIsSubTypeDisabled(subTypesForSelected.length === 0);
    }
  }, [isEditMode, currentTask]);

  // Fetch task on mount (edit mode only — 'new' is the create sentinel, not an id)
  useEffect(() => {
    if (currentTaskId && currentTaskId !== 'new') {
      fetchTask({ variables: { where: { id: currentTaskId } } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load teams for the current user's org (team-based assignment replaces assignee org)
  useEffect(() => {
    const orgId = currentUser?.role?.org?.id;
    if (orgId) fetchTeamsByOrg(orgId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.role?.org?.id]);

  // Handle linked project from URL
  useEffect(() => {
    if (linkedProjectId && currentTaskId === 'new') {
      fetchProject({ variables: { where: { id: linkedProjectId } } });
      form.setFieldsValue({ linkedProjectId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedProjectId]);

  // Handle linked campaign from URL
  useEffect(() => {
    if (linkedCampaignId && currentTaskId === 'new') {
      fetchCampaignForTask({ variables: { id: linkedCampaignId } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedCampaignId]);

  // Set linked campaign in form
  useEffect(() => {
    if (linkedCampaign && currentTaskId === 'new' && linkedCampaignId) {
      const formattedCampaign = {
        id: linkedCampaign.id,
        name: linkedCampaign.name,
        startDate: moment(linkedCampaign.startDate),
        endDate: moment(linkedCampaign.endDate),
      };
      setRelatedCampaigns([formattedCampaign]);
      form.setFieldsValue({ relatedCampaigns: [formattedCampaign] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedCampaign]);

  // Cleanup linked campaign on unmount — only when THIS instance loaded one.
  // `linkedCampaign` is shared TaskProvider state; an unconditional clear here would
  // wipe it for other surfaces (e.g. closing the create modal over the board).
  useEffect(() => {
    if (!linkedCampaignId) return undefined;
    return () => setLinkedCampaign(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add linked project to projects list
  useEffect(() => {
    if (linkedProjectId && currentProject && currentTaskId === 'new') {
      setProjects((prev) => {
        const exists = prev.find((p) => p.id === linkedProjectId);
        if (exists) return prev;
        return [currentProject, ...prev];
      });
      setSelectedProjectId(linkedProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProject, linkedProjectId]);

  // Assignable people come from every project the task is on, grouped by project (primary
  // first). getTaskProjectAssigneeGroups drops disabled users and lists anyone who sits on
  // several of them once, under the first.
  useEffect(() => {
    const formProjectId = form.getFieldValue('linkedProjectId');
    if (!formProjectId) {
      setSelectedProjectId(null);
      setProjectAssigneeGroups([]);
      return;
    }
    setSelectedProjectId(formProjectId);
    const findProject = (id) => projects.find((p) => p.id === id) || (currentProject?.id === id ? currentProject : null);
    const selected = [formProjectId, ...selectedAdditionalProjectIds].map(findProject).filter(Boolean);
    setProjectAssigneeGroups(getTaskProjectAssigneeGroups(selected));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.getFieldValue('linkedProjectId'), selectedAdditionalProjectIds, projects, currentProject]);

  // Update org lists when fetched. Keyed on orgTypeRequest (not just the data) because the lazy
  // query can return the SAME `basicOrgsDetails` reference for a cached result — e.g. reopening
  // the form and focusing the Advertiser/Channel Partner picker WITHOUT typing. With only
  // `[basicOrgsDetails]`, that unchanged reference wouldn't re-run this effect, leaving the
  // freshly-reset list empty until the user types (new vars → new data). Skip while loading so a
  // stale, wrong-type result is never routed into the wrong bucket mid-fetch.
  useEffect(() => {
    if (basicOrgsDetailsLoading) return;
    switch (orgTypeRequest) {
      case 'ASSIGNEE':
        setAssigneeOrgs(basicOrgsDetails);
        break;
      case 'CHANNEL_PARTNER':
        setChannelPartnerOrgs(basicOrgsDetails);
        break;
      case 'ADVERTISER':
        setAdvertiserOrgs(basicOrgsDetails);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicOrgsDetails, orgTypeRequest, basicOrgsDetailsLoading]);

  // Update assignee users when fetched
  useEffect(() => {
    setAssigneeUsers(basicUsersDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicUsersDetails]);

  // Update teams when fetched
  useEffect(() => {
    if (teamsData?.teams) {
      setTeams(teamsData.teams.filter((t) => !t.name.startsWith('__deleted__')));
    }
  }, [teamsData]);

  // Update projects list from connection data
  useEffect(() => {
    if (projectsConnectionData?.projectsConnection) {
      setProjects(projectsConnectionData.projectsConnection.edges.map((edge) => edge.node));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsConnectionData]);

  // Populate form when editing existing task
  useEffect(() => {
    if (currentTaskId && currentTaskId !== 'new' && currentTask && form) {
      if (currentTask.requestType) {
        setSelectedRequestType(currentTask.requestType);
      }
      const additionalIds = (currentTask.projects || [])
        .filter((p) => p.id !== currentTask.project?.id)
        .map((p) => p.id);
      setSelectedAdditionalProjectIds(additionalIds);
      form.setFieldsValue({
        name: currentTask.name,
        startDate: currentTask.startDate ? moment(currentTask.startDate) : null,
        dueDate: currentTask.dueDate ? moment(currentTask.dueDate) : null,
        requestType: currentTask.requestType,
        requestSubType: currentTask.requestSubType,
        requestTypePath: currentTask.requestSubType
          ? [currentTask.requestType, currentTask.requestSubType]
          : currentTask.requestType
            ? [currentTask.requestType]
            : undefined,
        linkUrl: currentTask.linkUrl,
        urgency: currentTask.urgency,
        // The assignee control is a plain single Select keyed by user id (sourced from the
        // selected team's members). It can't resolve the old labelInValue [{key,label}] shape —
        // that left the field blank and made submit skip it — so seed a plain id.
        assigneeUserId:
          currentTask.assigneeUsers?.[0]?.id || currentTask.assigneeUser?.id || undefined,
        channelPartnerId: currentTask.channelPartner?.id || null,
        advertiserId: currentTask.advertiser?.id || null,
        clientPath:
          level === 'PLATFORM' && currentTask.channelPartner?.id
            ? [currentTask.channelPartner.id, currentTask.advertiser?.id].filter(Boolean)
            : currentTask.advertiser?.id
              ? [currentTask.advertiser.id]
              : undefined,
        taskDetails: currentTask.taskDetails,
        relatedCampaigns: currentTask.relatedCampaigns?.length
          ? currentTask.relatedCampaigns.map((c) => ({
              ...c,
              startDate: moment(c.startDate),
              endDate: moment(c.endDate),
            }))
          : [],
        linkedProjectId: currentTask.project?.id || null,
        additionalProjectIds: additionalIds,
        teamId: currentTask.teams?.length > 0 ? currentTask.teams[0].id : null,
      });

      // Populate request-type-specific fields from requestTypeDetails
      const editRequestTypeDef = getRequestTypeDef(currentTask.requestType);
      if (editRequestTypeDef && currentTask.requestTypeDetails) {
        form.setFieldsValue(
          editRequestTypeDef.populate(currentTask.requestTypeDetails, currentTask)
        );
      }

      // Teams load via the current-user-org effect; just select the task's team
      if (currentTask.teams?.length > 0) {
        setSelectedTeamId(currentTask.teams[0].id);
      }

      setRelatedCampaigns(
        currentTask.relatedCampaigns?.length
          ? currentTask.relatedCampaigns.map((c) => ({
              ...c,
              startDate: moment(c.startDate),
              endDate: moment(c.endDate),
            }))
          : []
      );

      setAssigneeOrgs((prev) =>
        prev.concat(currentTask.assigneeOrg ? [currentTask.assigneeOrg] : [])
      );
      setChannelPartnerOrgs((prev) =>
        prev.concat(currentTask.channelPartner ? [currentTask.channelPartner] : [])
      );
      setAdvertiserOrgs((prev) =>
        prev.concat(currentTask.advertiser ? [currentTask.advertiser] : [])
      );
      setAssigneeUsers((prev) =>
        prev.concat(currentTask.assigneeUser ? [currentTask.assigneeUser] : [])
      );
      // Seed the option list with everything the task is already linked to, so both selects can
      // render names for projects that aren't in the fetched page.
      setProjects((prev) => {
        const linked = [...(currentTask.project ? [currentTask.project] : []), ...(currentTask.projects || [])];
        const merged = [...prev];
        linked.forEach((project) => {
          if (!merged.find((p) => p.id === project.id)) merged.push(project);
        });
        return merged;
      });

      setAttachedFiles(currentTask.attachedFiles || []);
      setAttachedImages(currentTask.attachedImages || []);
      setAttachedCodeSnippet(currentTask.codeSnippet || '');

      setDueDateRecurringConfig({
        recurring: currentTask.recurring,
        recurrenceType: currentTask.recurrenceType,
        dateOfMonth: currentTask.dateOfMonth,
        dayOfMonth: currentTask.dayOfMonth,
        dayOfWeek: currentTask.dayOfWeek,
        monthlyType: currentTask.monthlyType,
        weeklyDays: currentTask.weeklyDays,
        yearlyDate: currentTask.yearlyDate,
        yearlyMonth: currentTask.yearlyMonth,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTask]);

  // Modal mode: keep a fresh snapshot of the whole draft (form values + loaded option lists +
  // attachments + derived state) so TaskCreateModal can hand it to the full-page form on a
  // transition (Open full page / resize to mobile) without losing anything entered.
  useEffect(() => {
    if (!isModal || !captureDraftRef) return;
    captureDraftRef.current = {
      values: form.getFieldsValue(true),
      selectedRequestType,
      selectedTeamId,
      selectedProjectId,
      relatedCampaigns,
      attachedFiles,
      attachedImages,
      attachedCodeSnippet,
      channelPartnerOrgs,
      advertiserOrgs,
      assigneeUsers,
      teams,
      projects,
    };
  });

  // Full-page create: pick up any draft handed over from the create modal, restoring the loaded
  // option lists (so org/team/assignee names render, not raw ids) before the values.
  useEffect(() => {
    if (isModal || isEditMode) return;
    const draft = consumeTaskFormDraft();
    if (!draft) return;
    if (draft.channelPartnerOrgs) setChannelPartnerOrgs(draft.channelPartnerOrgs);
    if (draft.advertiserOrgs) setAdvertiserOrgs(draft.advertiserOrgs);
    if (draft.assigneeUsers) setAssigneeUsers(draft.assigneeUsers);
    if (draft.teams) setTeams(draft.teams);
    if (draft.projects) setProjects(draft.projects);
    if (draft.relatedCampaigns) setRelatedCampaigns(draft.relatedCampaigns);
    if (draft.attachedFiles) setAttachedFiles(draft.attachedFiles);
    if (draft.attachedImages) setAttachedImages(draft.attachedImages);
    if (draft.attachedCodeSnippet) setAttachedCodeSnippet(draft.attachedCodeSnippet);
    if (draft.selectedRequestType) setSelectedRequestType(draft.selectedRequestType);
    if (draft.selectedTeamId) setSelectedTeamId(draft.selectedTeamId);
    if (draft.selectedProjectId) setSelectedProjectId(draft.selectedProjectId);
    if (draft.values) form.setFieldsValue(draft.values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================
  // RENDER
  // ============================================

  const requestTypeDef = getRequestTypeDef(selectedRequestType);
  const RequestTypeComponent = requestTypeDef && requestTypeDef.component;
  const hideDescription = Boolean(requestTypeDef && requestTypeDef.hideDescription);
  // Only campaign-related request types show the Related Campaigns linking section (config-driven).
  const showRelatedCampaigns = campaignLinkedRequestTypes.includes(selectedRequestType);

  // Types that replace the description with their own field (e.g. the IO Note) still
  // capture file/image attachments — rendered inside the type's card via this slot.
  const attachmentsSlot = hideDescription ? (
    <TaskDescriptionField
      attachmentsOnly
      form={form}
      currentTaskId={currentTaskId}
      textareaRef={textareaRef}
      fileInputRef={fileInputRef}
      imageInputRef={imageInputRef}
      attachedFiles={attachedFiles}
      attachedImages={attachedImages}
      setAttachedFiles={setAttachedFiles}
      setAttachedImages={setAttachedImages}
      setNewAttachedFiles={setNewAttachedFiles}
      setNewAttachedImages={setNewAttachedImages}
      handleFileDelete={handleFileDelete}
      handleInsertCode={handleInsertCode}
      handleKeyDown={handleKeyDown}
      insertText={insertText}
    />
  ) : null;

  // Only block on the shared taskLoading flag when WE are the ones loading a task
  // (edit mode) — the board's drawer shares this flag and must not blank the form.
  if (taskLoading && currentTaskId && currentTaskId !== 'new') {
    return <OverlaySpinner />;
  }

  if (!isModal) {
    document.title = currentTaskId && currentTaskId !== 'new' ? 'Update Task' : 'Create Task';
  }

  return (
    <div
      // Full-page form: fill the viewport with the card background so a short (single-section)
      // form doesn't leave the page's grey backdrop showing below it. The modal supplies its own
      // chrome, so it stays a plain wrapper there.
      style={
        isModal
          ? undefined
          : {
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100vh',
              backgroundColor: bg.card,
            }
      }
    >
      {/* The modal supplies its own title/chrome; the page header is route-mode only */}
      {!isModal && (
        <PageHeaderLayout
          title={currentTaskId && currentTaskId !== 'new' ? 'Update Task' : 'Create Task'}
          style={{
            fontSize: windowWidth < 768 ? fontSizeTokens.title : fontSizeTokens.heading,
            display: 'flex',
            border: 'none',
            paddingInline: windowWidth < 768 ? '4%' : '10%',
            paddingTop: '2vh',
          }}
        />
      )}
      <PageContentLayout
        style={{
          backgroundColor: bg.card,
          display: 'flex',
          justifyContent: 'center',
          paddingInline: isModal ? 0 : windowWidth < 768 ? '4%' : '10%',
          width: '100%',
          // Full page: grow to fill the flex-column wrapper (white to the bottom). Modal: as before.
          ...(isModal ? { height: '100%' } : { flex: 1 }),
        }}
      >
        <Form form={form} layout="vertical" className="task-form-modern task-form-container__form">
          {/* Linked Project Banner */}
          {linkedProjectId && currentProject && currentTaskId === 'new' && (
            <Row className="task-form-linked-banner">
              <Col span={24}>
                <Card className="task-form-linked-banner__card task-form-linked-banner__card--project">
                  <div className="task-flex task-flex--center task-gap--sm">
                    <Text className="task-form-linked-banner__text task-form-linked-banner__text--project">
                      <strong>Linked Project:</strong> {currentProject.name}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          )}

          {/* Linked Campaign Banner */}
          {linkedCampaignId && linkedCampaign && currentTaskId === 'new' && (
            <Row className="task-form-linked-banner">
              <Col span={24}>
                <Card className="task-form-linked-banner__card task-form-linked-banner__card--campaign">
                  <div className="task-flex task-flex--center task-gap--sm">
                    <Text className="task-form-linked-banner__text task-form-linked-banner__text--campaign">
                      <strong>Linked Campaign:</strong> {linkedCampaign.name}
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          )}

          <Row>
            {/* Task Basic Fields */}
            <TaskBasicFields
              form={form}
              level={level}
              windowWidth={windowWidth}
              userOrgName={userOrgName}
              channelPartnerOrgs={channelPartnerOrgs}
              advertiserOrgs={advertiserOrgs}
              basicOrgsDetailsLoading={basicOrgsDetailsLoading}
              fetchBasicOrgs={fetchBasicOrgs}
              selectedProjectId={selectedProjectId}
              filteredSubTypes={filteredSubTypes}
              isSubTypeDisabled={isSubTypeDisabled}
              handleSubTypeChange={handleSubTypeChange}
              setTaskDatePickerVisible={setTaskDatePickerVisible}
              setTaskStartDatePickerVisible={setTaskStartDatePickerVisible}
              requestTypeSelected={!!selectedRequestType}
            />

            {/* Everything below the core fields is revealed only after a request type is chosen */}
            {selectedRequestType && (
              <>
                {/* Request-type-specific fields (registry-driven) — shown first */}
                {requestTypeDef &&
                  (RequestTypeComponent ? (
                    <RequestTypeComponent form={form} attachmentsSlot={attachmentsSlot} />
                  ) : (
                    <DynamicRequestFields fields={requestTypeDef.fields} />
                  ))}

                {/* Task Description Field (replaced by the type's own field + attachmentsSlot
                    when the registry sets hideDescription, e.g. the IO Note) */}
                {!hideDescription && (
                <TaskDescriptionField
                  form={form}
                  currentTaskId={currentTaskId}
                  textareaRef={textareaRef}
                  fileInputRef={fileInputRef}
                  imageInputRef={imageInputRef}
                  attachedFiles={attachedFiles}
                  attachedImages={attachedImages}
                  setAttachedFiles={setAttachedFiles}
                  setAttachedImages={setAttachedImages}
                  setNewAttachedFiles={setNewAttachedFiles}
                  setNewAttachedImages={setNewAttachedImages}
                  handleFileDelete={handleFileDelete}
                  handleInsertCode={handleInsertCode}
                  handleKeyDown={handleKeyDown}
                  insertText={insertText}
                  mentionUsers={mentionUsers}
                />
                )}

                {/* Task Campaigns Field — only for campaign-related request types
                    (campaignLinkedRequestTypes); internal/support types + IO hide it */}
                {showRelatedCampaigns && (
                  <TaskCampaignsField
                    form={form}
                    hasCampaigns={hasCampaigns}
                    relatedCampaigns={relatedCampaigns}
                    setRelatedCampaigns={setRelatedCampaigns}
                    setRelatedCampaignModal={setRelatedCampaignModal}
                  />
                )}

                {/* Request Details (Team/Assignee, Linked Project, Urgency) — shown last */}
                <TaskAssignmentFields
                  form={form}
                  level={level}
                  currentTaskId={currentTaskId}
                  currentTask={isEditMode ? currentTask : null}
                  isEditMode={isEditMode}
                  selectedProjectId={selectedProjectId}
                  userOrgName={userOrgName}
                  userOrgId={currentUser?.role?.org?.id}
                  assigneeOrgs={assigneeOrgs}
                  assigneeUsers={assigneeUsers}
                  channelPartnerOrgs={channelPartnerOrgs}
                  advertiserOrgs={advertiserOrgs}
                  basicOrgsDetailsLoading={basicOrgsDetailsLoading}
                  basicUsersDetailsLoading={basicUsersDetailsLoading}
                  fetchBasicOrgs={fetchBasicOrgs}
                  fetchBasicUsers={fetchBasicUsers}
                  setShowCollaboratorModal={setShowCollaboratorModal}
                  setSelectedCollaboratorId={setSelectedCollaboratorId}
                  setShowFollowerModal={setShowFollowerModal}
                  setSelectedFollowerId={setSelectedFollowerId}
                  teams={teams}
                  teamsLoading={teamsLoading}
                  selectedTeamId={selectedTeamId}
                  setSelectedTeamId={setSelectedTeamId}
                  fetchTeamsByOrg={fetchTeamsByOrg}
                  linkedProjectId={linkedProjectId}
                  setSelectedProjectId={setSelectedProjectId}
                  projects={projects}
                  projectAssigneeGroups={projectAssigneeGroups}
                  setSelectedAdditionalProjectIds={setSelectedAdditionalProjectIds}
                  fetchProjects={fetchProjects}
                />
              </>
            )}

            {/* Form Actions */}
            <TaskFormActions
              history={history}
              onCancel={isModal ? onClose : undefined}
              level={level}
              currentTaskId={currentTaskId}
              buttonDisabled={buttonDisabled}
              setButtonDisabled={setButtonDisabled}
              createMoreTasks={createMoreTasks}
              setCreateMoreTasks={setCreateMoreTasks}
              handleSubmit={handleSubmit}
            />
          </Row>

          {/* Hidden relatedCampaigns Form.Item */}
          <Form.Item name="relatedCampaigns">
            <Input type="hidden" />
          </Form.Item>
        </Form>
      </PageContentLayout>

      {/* Date Picker Modals */}
      {taskDetailVisible && (
        <TaskDatePickerModal
          visible={taskDetailVisible}
          setVisible={setTaskDatePickerVisible}
          currentDueDateConfig={dueDateRecurringConfig}
          disabledDates={!selectedProjectId}
          slaDays={slaConfigMap[selectedRequestType] || 0}
          slaRequestType={selectedRequestType}
          onClose={(values) => {
            setDueDateRecurringConfig(values);
            form.setFieldsValue({ dueDate: values.dueDate });
            form.validateFields(['startDate', 'dueDate']);
          }}
        />
      )}

      {taskStartDatePickerVisible && (
        <TaskStartDatePickerModal
          visible={taskStartDatePickerVisible}
          setVisible={setTaskStartDatePickerVisible}
          currentStartDate={form.getFieldValue('startDate')}
          disabledDates={!selectedProjectId}
          onClose={(values) => {
            form.setFieldsValue({ startDate: values.startDate });
            form.validateFields(['startDate', 'dueDate']);
          }}
        />
      )}

      {/* Code Snippet Modal */}
      {showCodeSnippetModal && (
        <TaskCodeSnippetModal
          visible={showCodeSnippetModal}
          setVisible={setShowCodeSnippetModal}
          onEmbedSnippet={(codeSnippet) => setAttachedCodeSnippet(codeSnippet)}
        />
      )}

      {/* Related Campaigns Modal */}
      {showRelatedCampaignModal && (
        <TaskRelatedCampaignsModal
          level={level}
          visible={showRelatedCampaignModal}
          setVisible={setRelatedCampaignModal}
          channelPartnerOrgId={
            level !== 'PLATFORM'
              ? currentUser.role.org.id
              : form.getFieldValue('channelPartnerId')
          }
          advertiserOrgId={form.getFieldValue('advertiserId')}
          selectedCampaigns={relatedCampaigns}
          onAddSelections={(selectedCampaignOrders) => {
            setRelatedCampaigns(selectedCampaignOrders);
            form.setFieldsValue({ relatedCampaigns: selectedCampaignOrders });
          }}
        />
      )}

      {/* Collaborator/Follower Removal Modal */}
      {(showCollaboratorModal || showFollowerModal) && (
        <Modal
          centered
          footer={[
            <Button
              key="cancel"
              onClick={() => setShowCollaboratorModal(false) || setShowFollowerModal(false)}
              disabled={updateTaskLoading}
            >
              Cancel
            </Button>,
            <Button
              key="ok"
              type="primary"
              disabled={updateTaskLoading}
              onClick={async () => {
                if (showFollowerModal) {
                  try {
                    message.loading('Removing follower...', 0);
                    await updateTask({
                      variables: {
                        where: { id: currentTaskId },
                        data: { followers: { disconnect: [{ id: selectedFollowerId }] } },
                      },
                    });
                    message.destroy();
                    message.success('Follower removed successfully');
                    setShowFollowerModal(false);
                  } catch (error) {
                    message.error(`Failed to remove follower: ${error.message}`);
                  }
                } else {
                  try {
                    message.loading('Promoting collaborator to assignee...', 0);
                    await updateTask({
                      variables: {
                        where: { id: currentTaskId },
                        data: { collaborators: { disconnect: [{ id: selectedCollaboratorId }] } },
                      },
                    });
                    form.setFieldsValue({ assigneeUserId: selectedCollaboratorId });
                    message.destroy();
                    message.success('Collaborator promoted to assignee.');
                    setShowCollaboratorModal(false);
                  } catch (error) {
                    message.destroy();
                    message.error(`Failed to promote collaborator: ${error.message}`);
                  }
                }
              }}
            >
              {updateTaskLoading && <LoadingOutlined />}{' '}
              {showCollaboratorModal ? 'Promote' : 'Remove'}
            </Button>,
          ]}
          title={showCollaboratorModal ? 'Promote Collaborator to Assignee' : 'Remove Follower'}
          visible={showCollaboratorModal || showFollowerModal}
        >
          <p>
            {showCollaboratorModal
              ? 'This user is currently a collaborator. Promote them to assignee? They will be automatically removed from the collaborator list.'
              : 'This user is a follower. Do you want to promote them as an assignee and remove them from followers?'}
          </p>
        </Modal>
      )}
    </div>
  );
};

export default withRouter(TaskFormContainer);
